/**
 * Central icon set (inline SVG, 24×24 stroke icons). Use `ICONS.name` — never emoji or ad-hoc SVG.
 * Decorative by default (`aria-hidden`); pass `title` to make an icon meaningful to screen readers.
 */
import type { ReactNode, SVGProps } from 'react'

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  size?: number
  title?: string
}

function makeIcon(displayName: string, paths: ReactNode) {
  function Icon({ size = 16, title, ...rest }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden={title ? undefined : true}
        role={title ? 'img' : undefined}
        focusable="false"
        {...rest}
      >
        {title ? <title>{title}</title> : null}
        {paths}
      </svg>
    )
  }
  Icon.displayName = displayName
  return Icon
}

/**
 * The official Microsoft logo (four squares, brand colours) for "Sign in with Microsoft". Filled,
 * not stroked, and drawn on its own 21×21 grid as Microsoft's sign-in branding guidelines specify.
 */
function MicrosoftLogo({ size = 21, title, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 21 21"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}
MicrosoftLogo.displayName = 'MicrosoftLogo'

export const ICONS = {
  close: makeIcon('CloseIcon', <path d="M18 6 6 18M6 6l12 12" />),
  info: makeIcon(
    'InfoIcon',
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5M12 8h.01" />
    </>,
  ),
  warning: makeIcon(
    'WarningIcon',
    <>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>,
  ),
  search: makeIcon(
    'SearchIcon',
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </>,
  ),
  refresh: makeIcon(
    'RefreshIcon',
    <>
      <path d="M20 11a8 8 0 0 0-14.9-3.9L4 8.5" />
      <path d="M4 4v4.5h4.5" />
      <path d="M4 13a8 8 0 0 0 14.9 3.9l1.1-1.4" />
      <path d="M20 20v-4.5h-4.5" />
    </>,
  ),
  sun: makeIcon(
    'SunIcon',
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </>,
  ),
  moon: makeIcon('MoonIcon', <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />),
  menu: makeIcon('MenuIcon', <path d="M4 6h16M4 12h16M4 18h16" />),
  logout: makeIcon(
    'LogoutIcon',
    <>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="m16 17 5-5-5-5M21 12H9" />
    </>,
  ),
  lock: makeIcon(
    'LockIcon',
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>,
  ),
  check: makeIcon('CheckIcon', <path d="m5 12 5 5L20 7" />),
  alert: makeIcon(
    'AlertIcon',
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </>,
  ),
  arrowRight: makeIcon('ArrowRightIcon', <path d="M5 12h14M13 6l6 6-6 6" />),
  chart: makeIcon('ChartIcon', <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />),
  users: makeIcon(
    'UsersIcon',
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18.5 20a6.5 6.5 0 0 0-3-5.5" />
    </>,
  ),
  plus: makeIcon('PlusIcon', <path d="M12 5v14M5 12h14" />),
  trash: makeIcon(
    'TrashIcon',
    <>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
    </>,
  ),
  shield: makeIcon('ShieldIcon', <path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6l-8-3Z" />),
  clock: makeIcon(
    'ClockIcon',
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
  ),
  globe: makeIcon(
    'GlobeIcon',
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </>,
  ),
  calendar: makeIcon(
    'CalendarIcon',
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>,
  ),
  grid: makeIcon(
    'GridIcon',
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>,
  ),
  ledger: makeIcon(
    'LedgerIcon',
    <>
      <path d="M5 3h11l3 3v15H5z" />
      <path d="M9 9h6M9 13h6M9 17h4" />
    </>,
  ),
  chevronDown: makeIcon('ChevronDownIcon', <path d="m6 9 6 6 6-6" />),
  chevronUp: makeIcon('ChevronUpIcon', <path d="m18 15-6-6-6 6" />),
  arrowUp: makeIcon('ArrowUpIcon', <path d="M12 19V5M6 11l6-6 6 6" />),
  arrowDown: makeIcon('ArrowDownIcon', <path d="M12 5v14M6 13l6 6 6-6" />),
  eye: makeIcon(
    'EyeIcon',
    <>
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </>,
  ),
  eyeOff: makeIcon(
    'EyeOffIcon',
    <>
      <path d="M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3 3.9M6.6 6.6A17 17 0 0 0 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
    </>,
  ),
  pin: makeIcon(
    'PinIcon',
    <>
      <path d="M12 17v5" />
      <path d="M9 3h6l-1 6 4 4v2H6v-2l4-4-1-6Z" />
    </>,
  ),
  merge: makeIcon('MergeIcon', <path d="M6 3v6a6 6 0 0 0 6 6h6M15 12l3 3-3 3M6 21v-6" />),
  microsoft: MicrosoftLogo,
} as const

export type IconName = keyof typeof ICONS
